import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BadgeCheck, Building2, Factory, MapPin, Maximize2, Minimize2, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import GlassCard from "../ui-custom/GlassCard";
import { footprintSites, exportRegions } from "../../data/mock";
import { WORLD_GRID, INDIA_GRID } from "../../data/footprintMapGrid";
import QpAiIntro from "./QpAiIntro";
import RouteColorCycle from "./RouteColorCycle";
import useMapZoom, { MAX_ZOOM } from "./useMapZoom";
import QpAiChat from "./QpAiChat";
import {
  QP_AI_KEYS,
  CLICKS_TO_UNLOCK,
  CLICK_WINDOW_MS,
  SPEED_STEPS,
  BOOST_GLOW_MS,
  WELCOME_MS,
  warmUpVoices,
  speakWelcome,
  playPowerUp
} from "./qpAi";

// Interactive group-footprint map: a dotted world map (overseas offices, Endoks
// in Türkiye, export routes out of Sangli) beside a zoomed India inset (where
// the group companies and domestic offices sit too close together to read at
// world scale). Pins are HTML buttons laid over the SVG by percentage, so they
// stay a fixed tappable size however the map scales.

const gridSize = (g) => ({ w: g.lonMax - g.lonMin, h: g.latMax - g.latMin });

// Land runs -> one SVG path of zero-length dashes, drawn as round dots.
const gridPath = ({ step, rows }) => {
  const parts = [];
  rows.split("|").forEach((row, r) => {
    if (!row) return;
    const y = ((r + 0.5) * step).toFixed(2);
    row.split(",").forEach((run) => {
      const [start, len] = run.split(".").map(Number);
      parts.push(`M${((start + 0.5) * step).toFixed(2)} ${y}h${((len - 1) * step + 0.01).toFixed(2)}`);
    });
  });
  return parts.join("");
};

const project = (g, lat, lon) => ({ x: lon - g.lonMin, y: g.latMax - lat });
const toPercent = (g, lat, lon) => {
  const { w, h } = gridSize(g);
  const { x, y } = project(g, lat, lon);
  return { left: (x / w) * 100, top: (y / h) * 100 };
};

const isProposed = (site) => site.companies?.some((c) => /proposed/i.test(c.role));

// Route glow timing: how long it lingers after the cursor leaves a location,
// and after a tap on touch screens.
const HOVER_GLOW_MS = 2500;
const TAP_GLOW_MS = 3000;

const KIND_LABEL = {
  hq: "Headquarters & Manufacturing",
  company: "Group Company",
  office: "Sales & Representative Office"
};

const PinMark = ({ site }) => {
  if (site.kind === "office") {
    return <span className="block w-[11px] h-[11px] rounded-full border-[2.5px] border-orange-500 bg-card" />;
  }
  if (isProposed(site)) {
    return <span className="block w-3.5 h-3.5 rounded-full border-2 border-dotted border-qcyan bg-card" />;
  }
  if (site.kind === "hq") {
    return (
      <span className="flex items-center justify-center w-4 h-4 rounded-full bg-qcyan shadow-[0_0_12px_rgba(255,0,15,0.6)]">
        <span className="w-1.5 h-1.5 rounded-full bg-white" />
      </span>
    );
  }
  return <span className="block w-3 h-3 rounded-full bg-qcyan border-2 border-card shadow-[0_0_10px_rgba(255,0,15,0.5)]" />;
};

// `inv` (1 / map zoom) keeps a pin the same on-screen size when the map is zoomed.
// onGlow/onRelease drive the route glow: on while hovered/focused, fading out
// after the cursor leaves; a tap (no hover) lets it fade after a few seconds.
const Pin = ({ site, grid, active, onSelect, onGlow = () => {}, onRelease = () => {}, labelClass = "", inv = 1 }) => {
  const { left, top } = toPercent(grid, site.lat, site.lon);
  const [dx, dy] = site.nudge || [0, 0];
  const labelLeft = site.labelSide === "left";
  return (
    <button
      type="button"
      onClick={(e) => {
        onSelect(site.id);
        onGlow(site.id);
        if (e.nativeEvent.pointerType && e.nativeEvent.pointerType !== "mouse") onRelease(TAP_GLOW_MS);
      }}
      onMouseEnter={() => {
        onSelect(site.id);
        onGlow(site.id);
      }}
      onMouseLeave={() => onRelease()}
      onFocus={() => {
        onSelect(site.id);
        onGlow(site.id);
      }}
      onBlur={() => onRelease()}
      aria-label={`${site.city}, ${site.region}`}
      aria-pressed={active}
      className={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-qcyan ${active ? "z-20" : "z-10"}`}
      style={{
        left: `calc(${left}% + ${dx * inv}px)`,
        top: `calc(${top}% + ${dy * inv}px)`,
        "--tw-scale-x": inv,
        "--tw-scale-y": inv,
        transition: "transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)"
      }}
    >
      {active && (
        <span
          className={`absolute inset-0.5 rounded-full opacity-50 motion-safe:animate-ping ${site.kind === "office" ? "bg-orange-500" : "bg-qcyan"}`}
        />
      )}
      <span className="relative">
        <PinMark site={site} />
      </span>
      <span
        className={`absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] sm:text-[11px] font-semibold pointer-events-none [text-shadow:0_0_3px_hsl(var(--card)),0_0_3px_hsl(var(--card))] ${
          labelLeft ? "right-full mr-0.5" : "left-full ml-0.5"
        } ${active ? "text-foreground" : "text-muted-foreground"} ${labelClass}`}
      >
        {site.city}
      </span>
    </button>
  );
};

const LegendItem = ({ mark, label }) => (
  <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
    {mark}
    {label}
  </span>
);

const Detail = ({ site }) => (
  <div className="rounded-xl border border-border bg-card/60 p-5">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
      {site.kind === "office" ? <Building2 size={14} className="text-orange-500" /> : <Factory size={14} className="text-qcyan" />}
      {KIND_LABEL[site.kind]}
    </div>
    <h3 className="font-display font-semibold text-foreground text-lg leading-tight">{site.city}</h3>
    <p className="text-sm text-muted-foreground mb-4">{site.region}</p>
    {site.kind === "office" ? (
      <p className="text-sm text-muted-foreground leading-relaxed">
        Sales and customer support for {site.region === "India" ? "customers across India" : `customers in ${site.region} and the surrounding region`}, backed by the
        group's manufacturing in India and Türkiye.
      </p>
    ) : (
      <div className="space-y-4">
        {site.companies.map((c) => (
          <div key={c.name}>
            <p className="font-semibold text-foreground text-sm">{c.name}</p>
            <p className="text-xs text-qamber uppercase tracking-wide mt-0.5">{c.role}</p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">{c.focus}</p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
              {c.since && <span>In the group since {c.since}</span>}
              {c.nabl && (
                <span className="inline-flex items-center gap-1 text-qcyan">
                  <BadgeCheck size={13} /> NABL accredited
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Fullscreen for the world view. Uses the browser Fullscreen API where it
// exists; where it doesn't (iPhone Safari only allows it on <video>) it falls
// back to a fixed full-viewport overlay, so the button works everywhere. Esc
// exits either way - natively for the API, via a keydown listener otherwise.
const useFullscreen = (ref) => {
  const [native, setNative] = useState(false);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const onChange = () => setNative(document.fullscreenElement === ref.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [ref]);

  useEffect(() => {
    if (!fallback) return undefined;
    const onKey = (e) => e.key === "Escape" && setFallback(false);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [fallback]);

  const toggle = useCallback(() => {
    if (native) {
      document.exitFullscreen?.();
    } else if (fallback) {
      setFallback(false);
    } else if (ref.current?.requestFullscreen && document.fullscreenEnabled) {
      ref.current
        .requestFullscreen()
        .then(() => {
          // Phones: go landscape. Works on Android; iPhone has no lock (and no
          // element fullscreen) - there the map is rotated instead, see below.
          if (window.matchMedia("(pointer: coarse)").matches) screen.orientation?.lock?.("landscape").catch(() => {});
        })
        .catch(() => setFallback(true));
    } else {
      setFallback(true);
    }
  }, [native, fallback, ref]);

  return { isFullscreen: native || fallback, isFallback: fallback, toggle };
};

const FootprintMap = () => {
  const [activeId, setActiveId] = useState("sangli");
  const worldRef = useRef(null);
  const { isFullscreen, isFallback, toggle: toggleFullscreen } = useFullscreen(worldRef);
  // Fullscreen on a phone held upright (the orientation couldn't be locked,
  // e.g. iPhone): show the map rotated 90 degrees so it's landscape anyway.
  const [portraitTouch, setPortraitTouch] = useState(false);
  useEffect(() => {
    const q = window.matchMedia("(orientation: portrait) and (pointer: coarse)");
    const update = () => setPortraitTouch(q.matches);
    update();
    q.addEventListener?.("change", update);
    return () => q.removeEventListener?.("change", update);
  }, []);
  const rotated = isFullscreen && portraitTouch;
  // Height available to the map in fullscreen - the screen's width when rotated.
  const fsHeight = rotated ? "100vw" : "100dvh";

  // QP AI: hidden keys on the fullscreen map, armed once the route colour
  // sequence has played through. Five quick clicks on a key start the intro,
  // which hands over to the chat. Leaving fullscreen always returns to the map.
  const [aiPhase, setAiPhase] = useState("off"); // off | intro | welcome | chat
  const [aiOrigin, setAiOrigin] = useState(null);
  const [keysArmed, setKeysArmed] = useState(false);
  const keyClicks = useRef({ key: -1, count: 0, last: 0 });
  // Zoom / pan: fullscreen map only (not during the QP AI sequence).
  const { boxRef: zoomBoxRef, view: zoomView, innerStyle: zoomStyle, handlers: zoomHandlers, zoomBy, reset: resetZoom } =
    useMapZoom(isFullscreen && aiPhase === "off", rotated);
  const inv = 1 / zoomView.z;

  // Route colour sequence (RouteColorCycle.jsx). The SVG draws the original red
  // animation (its dash offset driven from the same clock); during the colour,
  // gold and black phases the canvas takes over. rateRef is the speed multiplier.
  const svgRoutesRef = useRef(null);
  const rateRef = useRef(1);
  // The sequence clock lives here, not in the canvas, so it keeps counting if
  // the map remounts (e.g. the phone fullscreen overlay) - the QP AI key arms
  // exactly when the black phase ends, however fast the sequence was run.
  const clockRef = useRef({ vt: 0 });
  // Route currently glowing (hovered/tapped location), read by the canvas each frame.
  const activeRouteRef = useRef(null);
  const zoomLayerRef = useRef(null);
  const [routeCycle, setRouteCycle] = useState({ phase: "original", done: false });
  const onRoutePhase = useCallback((p) => {
    setRouteCycle(p);
    if (p.done) setKeysArmed(true);
  }, []);

  // Sangli key (fullscreen only, live from the start): 5 quick clicks -> a 2 s
  // golden glow from Sangli; after that each further click steps the dash
  // speed up through SPEED_STEPS (to 5x), with a small gold pulse per step.
  // Leaving fullscreen resets it all back to normal speed.
  const sangliClicks = useRef({ count: 0, last: 0 });
  const glowTimer = useRef(null);
  const [boost, setBoost] = useState({ stage: "off", level: 0, pulse: 0 }); // off | glow | ready

  const onSangliClick = () => {
    if (boost.stage === "glow") return;
    if (boost.stage === "ready") {
      if (boost.level >= SPEED_STEPS.length) return;
      rateRef.current = SPEED_STEPS[boost.level];
      setBoost((b) => ({ ...b, level: b.level + 1, pulse: b.pulse + 1 }));
      return;
    }
    const now = performance.now();
    const k = sangliClicks.current;
    const count = now - k.last <= CLICK_WINDOW_MS ? k.count + 1 : 1;
    sangliClicks.current = { count, last: now };
    if (count >= CLICKS_TO_UNLOCK) {
      sangliClicks.current = { count: 0, last: 0 };
      setBoost((b) => ({ ...b, stage: "glow" }));
      glowTimer.current = setTimeout(() => setBoost((b) => ({ ...b, stage: "ready" })), BOOST_GLOW_MS);
    }
  };

  useEffect(() => {
    if (isFullscreen) return;
    setAiPhase("off");
    clearTimeout(glowTimer.current);
    sangliClicks.current = { count: 0, last: 0 };
    rateRef.current = 1;
    setBoost({ stage: "off", level: 0, pulse: 0 });
  }, [isFullscreen]);

  useEffect(() => () => clearTimeout(glowTimer.current), []);

  // Once the dots have gone: the voice and title, then the chat.
  useEffect(() => {
    if (aiPhase !== "welcome") return undefined;
    speakWelcome();
    const timer = setTimeout(() => setAiPhase((p) => (p === "welcome" ? "chat" : p)), WELCOME_MS);
    return () => clearTimeout(timer);
  }, [aiPhase]);

  const onKeyClick = (i, key) => {
    const now = performance.now();
    const k = keyClicks.current;
    const count = k.key === i && now - k.last <= CLICK_WINDOW_MS ? k.count + 1 : 1;
    keyClicks.current = { key: i, count, last: now };
    if (count === 1) warmUpVoices();
    if (count >= CLICKS_TO_UNLOCK) {
      keyClicks.current = { key: -1, count: 0, last: 0 };
      setAiOrigin(project(WORLD_GRID, key.lat, key.lon));
      setAiPhase("intro");
      playPowerUp();
    }
  };
  const worldDots = useMemo(() => gridPath(WORLD_GRID), []);
  const indiaDots = useMemo(() => gridPath(INDIA_GRID), []);

  const worldSites = footprintSites.filter((s) => s.map === "world");
  const indiaSites = footprintSites.filter((s) => s.map === "india");
  const active = footprintSites.find((s) => s.id === activeId);

  const world = gridSize(WORLD_GRID);
  const india = gridSize(INDIA_GRID);
  const sangli = footprintSites.find((s) => s.id === "sangli");
  const origin = project(WORLD_GRID, sangli.lat, sangli.lon);
  // Export routes: Sangli out to every overseas site and named export market.
  // Memoised so the route canvas isn't rebuilt on every render; the inputs are static data.
  const routes = useMemo(() => [...worldSites, ...exportRegions].map((d) => {
    const end = project(WORLD_GRID, d.lat, d.lon);
    const dist = Math.hypot(end.x - origin.x, end.y - origin.y);
    const cx = (origin.x + end.x) / 2;
    const cy = (origin.y + end.y) / 2 - dist * 0.3;
    return {
      key: d.id || d.label,
      d: `M${origin.x} ${origin.y}Q${cx} ${cy} ${end.x} ${end.y}`,
      p0: origin,
      c: { x: cx, y: cy },
      p1: end,
      glow: d.kind === "office" ? "#F97316" : "#FF000F"
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
  // Route glow: hovering (or tapping) an overseas location makes its route from
  // Sangli glow and brighten on top of the normal animation - nothing else
  // changes - then it fades back HOVER_GLOW_MS after the cursor leaves. It's
  // separate from activeId, which keeps the details panel on the last pick.
  const [glow, setGlow] = useState({ key: null, on: false });
  const glowTimerRef = useRef(null);
  const glowRoute = useCallback(
    (id) => {
      if (!routes.some((r) => r.key === id)) return;
      clearTimeout(glowTimerRef.current);
      activeRouteRef.current = id;
      setGlow({ key: id, on: true });
    },
    [routes]
  );
  const releaseRoute = useCallback((delay = HOVER_GLOW_MS) => {
    clearTimeout(glowTimerRef.current);
    glowTimerRef.current = setTimeout(() => {
      activeRouteRef.current = null;
      setGlow((g) => ({ ...g, on: false }));
    }, delay);
  }, []);
  useEffect(() => () => clearTimeout(glowTimerRef.current), []);
  const glowRouteDef = routes.find((r) => r.key === glow.key) || null;
  const insetBox = {
    ...project(WORLD_GRID, INDIA_GRID.latMax, INDIA_GRID.lonMin),
    w: india.w,
    h: india.h
  };

  // World view - this wrapper is what goes fullscreen.
  const worldView = (
    <div
      ref={worldRef}
      className={`min-w-0 ${isFallback ? "fixed inset-0 z-[100] overflow-auto" : "relative"} ${isFullscreen ? "bg-background" : ""}`}
    >
      <div
        className={`relative ${isFullscreen ? "flex flex-col items-center justify-center gap-4 p-4 sm:p-8" : ""} ${
          isFullscreen && !rotated ? "w-full h-full" : ""
        }`}
        style={
          rotated
            ? {
                position: "absolute",
                top: 0,
                left: "100%",
                width: "100dvh",
                height: "100vw",
                transform: "rotate(90deg)",
                transformOrigin: "top left"
              }
            : undefined
        }
      >
      {isFullscreen && aiPhase === "off" && (
        <div className="w-full flex flex-wrap items-center gap-x-5 gap-y-2 pr-14 sm:pr-40">
          <span className="section-eyebrow">Group Footprint</span>
          <LegendItem mark={<span className="w-3 h-3 rounded-full bg-qcyan" />} label="Manufacturing & group companies" />
          <LegendItem mark={<span className="w-[11px] h-[11px] rounded-full border-[2.5px] border-orange-500" />} label="Sales & representative offices" />
          <LegendItem mark={<span className="w-5 border-t-2 border-dashed border-qcyan/60" />} label="Export routes" />
        </div>
      )}
      <button
        type="button"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? "Exit fullscreen" : "View map fullscreen"}
        title={isFullscreen ? "Exit fullscreen (Esc)" : "View fullscreen"}
        className={`absolute z-30 inline-flex items-center gap-1.5 rounded-full border border-border bg-card/90 backdrop-blur px-3 py-1.5 text-xs font-medium text-foreground shadow-sm hover:border-qcyan hover:text-qcyan transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-qcyan ${
          isFullscreen ? "top-4 right-4 sm:top-6 sm:right-6" : "top-0 right-0"
        }`}
      >
        {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        <span className="hidden sm:inline">{isFullscreen ? "Exit fullscreen" : "Fullscreen"}</span>
      </button>
      {aiPhase === "chat" ? (
        <div className="w-full max-w-5xl" style={{ height: `min(calc(${fsHeight} - 6rem), 760px)` }}>
          <QpAiChat onClose={() => setAiPhase("off")} />
        </div>
      ) : (
        <div
          ref={zoomBoxRef}
          {...zoomHandlers}
          className={`relative w-full ${
            isFullscreen ? `overflow-hidden touch-none select-none ${zoomView.z > 1 ? "cursor-grab active:cursor-grabbing" : ""}` : "cursor-zoom-in"
          }`}
          title={isFullscreen ? undefined : "Click the map to view it fullscreen"}
          // Clicking empty map space (not a pin) opens fullscreen.
          onClick={(e) => {
            if (!isFullscreen && !e.target.closest("button")) toggleFullscreen();
          }}
          style={{
            aspectRatio: `${world.w} / ${world.h}`,
            // In fullscreen, fit whichever screen edge runs out first.
            ...(isFullscreen && { width: `min(100%, calc((${fsHeight} - 7rem) * ${world.w} / ${world.h}))` })
          }}
        >
          {/* Zoomable layers - the map below the route canvas, pins and keys above it */}
          <div ref={zoomLayerRef} className="absolute inset-0" style={zoomStyle}>
          <svg
            viewBox={`0 0 ${world.w} ${world.h}`}
            className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${aiPhase === "intro" || aiPhase === "welcome" ? "opacity-0" : ""}`}
            aria-hidden="true"
          >
            <path
              d={worldDots}
              className="text-muted-foreground"
              stroke="currentColor"
              strokeOpacity={0.35}
              strokeWidth={0.9}
              strokeLinecap="round"
              strokeDasharray={`0 ${WORLD_GRID.step}`}
              fill="none"
            />
            <rect
              x={insetBox.x}
              y={insetBox.y}
              width={insetBox.w}
              height={insetBox.h}
              rx={1.5}
              fill="rgba(255,0,15,0.05)"
              stroke="rgba(255,0,15,0.45)"
              strokeWidth={0.35}
              strokeDasharray="1.5 1"
            />
            <text x={insetBox.x + 1.2} y={insetBox.y + insetBox.h + 4} fontSize={3.2} className="fill-muted-foreground" letterSpacing={0.4}>
              INDIA (DETAIL)
            </text>
            {glowRouteDef && (
              <g
                fill="none"
                stroke={glowRouteDef.glow}
                strokeLinecap="round"
                style={{ opacity: glow.on ? 1 : 0, transition: `opacity ${glow.on ? 400 : 1400}ms ease` }}
              >
                <g className="qp-route-highlight">
                  <path d={glowRouteDef.d} strokeWidth={2.6} strokeOpacity={0.16} />
                  <path d={glowRouteDef.d} strokeWidth={1.2} strokeOpacity={0.3} />
                  <path d={glowRouteDef.d} strokeWidth={0.5} strokeOpacity={0.45} />
                </g>
              </g>
            )}
            <g
              ref={svgRoutesRef}
              className={`text-qcyan ${routeCycle.phase === "original" ? "" : "opacity-0"} ${
                boost.stage === "glow" ? "qp-gold-routes" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeOpacity={0.5}
              strokeWidth={0.4}
            >
              {routes.map((r) => (
                <path
                  key={r.key}
                  d={r.d}
                  strokeDasharray="2 1"
                  // the glowing route's dashes brighten too
                  strokeOpacity={glow.on && r.key === glow.key ? 1 : undefined}
                  style={{ transition: "stroke-opacity 1s ease" }}
                />
              ))}
            </g>
            {indiaSites.map((s) => {
              const p = project(WORLD_GRID, s.lat, s.lon);
              return <circle key={s.id} cx={p.x} cy={p.y} r={0.8} fill={s.kind === "office" ? "#F97316" : "#FF000F"} />;
            })}
            {exportRegions.map((r) => {
              const p = project(WORLD_GRID, r.lat, r.lon);
              return (
                <g key={r.label}>
                  <circle cx={p.x} cy={p.y} r={0.9} fill="none" stroke="#FF000F" strokeOpacity={0.6} strokeWidth={0.35} />
                  <text x={p.x + 1.8} y={p.y + 1.1} fontSize={3} className="fill-muted-foreground">
                    {r.label}
                  </text>
                </g>
              );
            })}
          </svg>
          </div>
          <RouteColorCycle
            routes={routes}
            width={world.w}
            height={world.h}
            rateRef={rateRef}
            clockRef={clockRef}
            activeRouteRef={activeRouteRef}
            svgRoutesRef={svgRoutesRef}
            viewLayerRef={zoomLayerRef}
            onPhase={onRoutePhase}
            className={`transition-opacity duration-500 ${aiPhase === "intro" || aiPhase === "welcome" ? "opacity-0" : ""} ${
              boost.stage === "glow" ? "qp-gold-routes" : ""
            }`}
          />
          <div className="absolute inset-0" style={zoomStyle}>
          <div className={`absolute inset-0 transition-opacity duration-500 ${aiPhase === "intro" || aiPhase === "welcome" ? "opacity-0 pointer-events-none" : ""}`}>
            {worldSites.map((s) => (
              <Pin
                key={s.id}
                site={s}
                grid={WORLD_GRID}
                active={s.id === activeId}
                onSelect={setActiveId}
                onGlow={glowRoute}
                onRelease={releaseRoute}
                labelClass="hidden sm:inline"
                inv={inv}
              />
            ))}
          </div>
          {/* Sangli: golden glow, then speed pulses */}
          {(boost.stage === "glow" || boost.pulse > 0) && (
            <div
              className="absolute pointer-events-none"
              style={{ left: `${(origin.x / world.w) * 100}%`, top: `${(origin.y / world.h) * 100}%` }}
              aria-hidden="true"
            >
              {boost.stage === "glow" && <span className="qp-sangli-glow" />}
              {boost.pulse > 0 && <span key={boost.pulse} className="qp-sangli-pulse" />}
            </div>
          )}
          {/* Hidden Sangli key - invisible, fullscreen only, live from the start */}
          {isFullscreen && aiPhase === "off" && (
            <span
              aria-hidden="true"
              onClick={onSangliClick}
              className="absolute z-30 w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${(origin.x / world.w) * 100}%`, top: `${(origin.y / world.h) * 100}%`, "--tw-scale-x": inv, "--tw-scale-y": inv }}
            />
          )}
          {/* Hidden QP AI keys - invisible, fullscreen only, armed once the route sequence has played */}
          {isFullscreen &&
            keysArmed &&
            aiPhase === "off" &&
            QP_AI_KEYS.map((key, i) => {
              const { left, top } = toPercent(WORLD_GRID, key.lat, key.lon);
              return (
                <span
                  key={i}
                  aria-hidden="true"
                  onClick={() => onKeyClick(i, key)}
                  className="absolute z-30 w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${left}%`, top: `${top}%`, "--tw-scale-x": inv, "--tw-scale-y": inv }}
                />
              );
            })}
          {aiPhase === "intro" && aiOrigin && (
            <QpAiIntro grid={WORLD_GRID} width={world.w} height={world.h} origin={aiOrigin} onDone={() => setAiPhase("welcome")} />
          )}
          </div>
          {isFullscreen && aiPhase === "off" && (
            <div className="absolute bottom-2 right-2 z-40 flex flex-col gap-1.5" onPointerDown={(e) => e.stopPropagation()}>
              {[
                { label: "Zoom in", icon: <ZoomIn size={15} />, run: () => zoomBy(1.6), off: zoomView.z >= MAX_ZOOM },
                { label: "Zoom out", icon: <ZoomOut size={15} />, run: () => zoomBy(1 / 1.6), off: zoomView.z <= 1 },
                { label: "Reset zoom", icon: <RotateCcw size={14} />, run: resetZoom, off: zoomView.z <= 1 }
              ].map((b) => (
                <button
                  key={b.label}
                  type="button"
                  aria-label={b.label}
                  title={b.label}
                  disabled={b.off}
                  onClick={b.run}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-border bg-card/90 backdrop-blur text-foreground shadow-sm hover:border-qcyan hover:text-qcyan disabled:opacity-40 disabled:hover:border-border disabled:hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-qcyan"
                >
                  {b.icon}
                </button>
              ))}
            </div>
          )}
          {aiPhase === "welcome" && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="qpai-welcome text-2xl sm:text-4xl md:text-6xl text-center px-4">Welcome to QP AI</p>
            </div>
          )}
        </div>
      )}
      {isFullscreen && aiPhase !== "chat" && (
        <p className="text-xs text-muted-foreground text-center">
          Scroll or pinch to zoom, drag to move · Press Esc or “Exit fullscreen” to return
        </p>
      )}
      </div>
    </div>
  );

  return (
    <GlassCard className="p-4 sm:p-6 md:p-8" whileHover={{}}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <span className="section-eyebrow">Group Footprint</span>
          <h2 className="font-display font-semibold text-foreground text-xl md:text-2xl mt-2">
            Manufacturing in India &amp; Türkiye, offices on four continents
          </h2>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <LegendItem mark={<span className="w-3 h-3 rounded-full bg-qcyan" />} label="Manufacturing & group companies" />
          <LegendItem mark={<span className="w-[11px] h-[11px] rounded-full border-[2.5px] border-orange-500" />} label="Sales & representative offices" />
          <LegendItem mark={<span className="w-5 border-t-2 border-dashed border-qcyan/60" />} label="Export routes" />
        </div>
      </div>

      <div className="space-y-6">
        {/* The fallback overlay is portalled to <body>: the card sits inside animated
            (transformed) wrappers, which would otherwise trap position:fixed. */}
        {isFallback ? createPortal(worldView, document.body) : worldView}

        {/* India inset + details */}
        <div className="min-w-0 grid md:grid-cols-2 gap-6 items-start">
          <div className="rounded-xl border border-border bg-background/40 p-2">
            <div className="relative w-full" style={{ aspectRatio: `${india.w} / ${india.h}` }}>
              <svg viewBox={`0 0 ${india.w} ${india.h}`} className="absolute inset-0 w-full h-full" aria-hidden="true">
                <path
                  d={indiaDots}
                  className="text-muted-foreground"
                  stroke="currentColor"
                  strokeOpacity={0.35}
                  strokeWidth={0.24}
                  strokeLinecap="round"
                  strokeDasharray={`0 ${INDIA_GRID.step}`}
                  fill="none"
                />
              </svg>
              <span className="absolute top-2 left-3 text-[10px] uppercase tracking-widest text-muted-foreground">India</span>
              {indiaSites.map((s) => (
                <Pin key={s.id} site={s} grid={INDIA_GRID} active={s.id === activeId} onSelect={setActiveId} />
              ))}
            </div>
          </div>
          {active && <Detail site={active} />}
        </div>
      </div>

      {/* Index of every location - an easy tap target on small screens, where pins sit close together */}
      <div className="mt-6 pt-5 border-t border-border flex flex-wrap gap-2">
        {footprintSites.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              setActiveId(s.id);
              glowRoute(s.id);
              releaseRoute(TAP_GLOW_MS);
            }}
            aria-pressed={s.id === activeId}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
              s.id === activeId ? "border-qcyan text-foreground bg-qcyan/10" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            <MapPin size={12} className={s.kind === "office" ? "text-orange-500" : "text-qcyan"} />
            {s.city}
          </button>
        ))}
      </div>
    </GlassCard>
  );
};

export default FootprintMap;
